import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { Table, Spinner, Dropdown } from 'react-bootstrap';
import { EllipsisV } from '@styled-icons/fa-solid';
import DataTable, { useDataTableHandle } from '../../DataTable';
import { postCompanyPortsLazy, deleteCompanyPorts, getNewLoad } from '../../apiCalls';
import PortsModal from './portsModal';
import ConfirmationModal from '../../ConfirmationModal';
import ToastNotification from '../../Toast/ToastNotification';
import { RefDataContext } from '../Maintenance';

/** Build filter object with plain keys (portType, portName, etc.) for the backend. Strips "filter_" prefix if present. */
function buildBackendFilter(search, simpleFilter) {
  const merged = { ...search, ...(simpleFilter && typeof simpleFilter === 'object' ? simpleFilter : {}) };
  const out = {};
  Object.entries(merged).forEach(([key, value]) => {
    const plainKey = key.startsWith('filter_') ? key.slice(7) : key;
    out[plainKey] = value;
  });
  return out;
}

const Port = () => {
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [directions, setDirections] = useState([]);
  const [selectedPort, setSelectedPort] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ type: 'success', message: '' });
  const lastSearchRef = useRef(null);
  const lastFilterRef = useRef(null);

  const dth = useDataTableHandle();
  const { registerAddHandler } = useContext(RefDataContext);

  useEffect(() => {
    registerAddHandler('port', () =>
      setSelectedPort({
        id: -1,
        portName: '',
        portType: '',
        exitPort: '',
        EntryPort: '',
        portProcess: '',
        custOffCode: '',
        GVMSGoodsLocation: '',
        InvLinkedGoodsLocation: '',
        AltGoodsLocation: '',
      })
    );
  }, [registerAddHandler]);

  const fetchDirections = useCallback(async () => {
    try {
      const movementTypes = await getNewLoad('movement');
      setDirections(movementTypes || []);
    } catch (err) {
      console.error('Failed to load directions', err);
    }
  }, []);

  useEffect(() => {
    fetchDirections();
  }, [fetchDirections]);

  const updateData = useCallback(async (setPaginationLength, pagination, sortStatus, search, filter) => {
    lastSearchRef.current = search ?? null;
    lastFilterRef.current = filter ?? null;
    const backendFilter = buildBackendFilter(search ?? {}, filter ?? {});
    const payload = {
      skip: pagination.pageNo * pagination.itemsPerPage,
      amount: pagination.itemsPerPage,
      sort_by: sortStatus?.currentKey ?? 'portName',
      sort_order: sortStatus?.direction ?? 'asc',
      filter: Object.keys(backendFilter).length ? backendFilter : undefined,
    };
    try {
      setLoading(true);
      const result = await postCompanyPortsLazy(payload);
      setPorts(result.data ?? []);
      setPaginationLength(result.totalItems ?? 0);
    } catch (err) {
      console.error('Failed to load port codes', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPorts = useCallback(() => {
    if (!dth?.current) return;
    updateData(
      dth.current.setServerDataLength,
      dth.current.pagination,
      dth.current.sortStatus,
      lastSearchRef.current ?? null,
      lastFilterRef.current ?? null
    );
  }, [updateData, dth]);

  useEffect(() => {
    if (!paginationLoaded || !dth?.current?.pagination?.itemsPerPage) return;
    updateData(
      dth.current.setServerDataLength,
      dth.current.pagination,
      dth.current.sortStatus,
      lastSearchRef.current ?? null,
      lastFilterRef.current ?? null
    );
  }, [paginationLoaded, updateData]);

  const directionMap = useMemo(() => {
    const map = new Map();
    directions?.forEach((d) => map.set(Number(d.id), d.data));
    return map;
  }, [directions]);

  const normalizedPorts = useMemo(() => {
    return (ports || []).map((port) => {
      const exitPort = port.exitPort ?? port.exit ?? port.exitport;
      const entryPort = port.EntryPort ?? port.entryPort ?? port.entry;
      let normalizedEntry = entryPort;
      if (Number(exitPort) === 2) normalizedEntry = 3;
      return {
        ...port,
        exitPort: exitPort ? Number(exitPort) : '',
        EntryPort: normalizedEntry ? Number(normalizedEntry) : '',
      };
    });
  }, [ports]);

  const handleDelete = async (id) => {
    try {
      await deleteCompanyPorts(id);
      setShowConfirmDelete(null);
      setToastConfig({ type: 'success', message: 'Port Code deleted successfully' });
      setShowToast(true);
      refreshPorts();
    } catch (err) {
      setToastConfig({ type: 'error', message: 'Error deleting Port Code' });
      setShowToast(true);
    }
  };

  return (
    <>
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <PortsModal
        show={!!selectedPort}
        port={selectedPort}
        directions={directions}
        existingPorts={normalizedPorts}
        onHide={() => setSelectedPort(null)}
        onSaved={(isNew) => {
          setToastConfig({
            type: 'success',
            message: isNew ? 'New Port Code added' : 'Port Code updated',
          });
          setShowToast(true);
          setSelectedPort(null);
          refreshPorts();
        }}
        onError={(message) => {
          setToastConfig({ type: 'error', message: message || 'Duplicate Port Code for selected route and port' });
          setShowToast(true);
        }}
      />

      {loading && (
        <span className="d-flex align-items-center gap-2 mb-2">
          <Spinner size="sm" animation="border" /> Loading...
        </span>
      )}

      <DataTable
        idKey="id"
        handle={dth}
        data={normalizedPorts}
        className="table-striped"
        sortable
        filterable
        lazyLoading
        advancedSearch
        showAdvnSearch
        onUpdate={updateData}
        onPaginationLoaded={() => setPaginationLoaded(true)}
        isLoading={loading}
        handleReset={() => {
          lastSearchRef.current = null;
          lastFilterRef.current = null;
        }}
      >
        <DataTable.Headers
          style={{
            fontSize: '14.4px',
            backgroundColor: '#fff',
            paddingBottom: '10px',
            borderBottom: '1px solid #ddd',
            borderTop: '1px solid #ddd',
            whiteSpace: 'nowrap',
          }}
        />
        <DataTable.Pagination itemsPerPage={[10, 25, 50, 100]} className="w-100 d-flex justify-content-end" />
        <DataTable.Row style={{ fontSize: '15px' }} />
        <DataTable.Column
          colKey="portName"
          header="Port Name"
          searchOpts={{ type: 'text', sendAs: 'filter_portName' }}
        />
        <DataTable.Column
          colKey="portType"
          header="Port Type"
          searchOpts={{ type: 'text', sendAs: 'filter_portType' }}
        />
        <DataTable.Column
          colKey="exitPort"
          header="Exit Port"
          children={(item) => directionMap.get(item.exitPort) || item.exitPort || '-'}
          searchOpts={{ type: 'text', sendAs: 'filter_exitPort' }}
        />
        <DataTable.Column
          colKey="EntryPort"
          header="Entry Port"
          children={(item) => directionMap.get(item.EntryPort) || item.EntryPort || '-'}
          searchOpts={{ type: 'text', sendAs: 'filter_entryPort' }}
        />
        <DataTable.Column
          colKey="portProcess"
          header="Route / Port Process"
          searchOpts={{ type: 'text', sendAs: 'filter_portProcess' }}
        />
        <DataTable.Column
          colKey="custOffCode"
          header="Customs Office Code"
          searchOpts={{ type: 'text', sendAs: 'filter_customsOfficeCode' }}
        />
        <DataTable.Column
          colKey="GVMSGoodsLocation"
          header="GVMS Goods Location"
          searchOpts={{ type: 'text', sendAs: 'filter_GVMSGoodsLocation' }}
        />
        <DataTable.Column
          colKey="InvLinkedGoodsLocation"
          header="INV Goods Location"
          searchOpts={{ type: 'text', sendAs: 'filter_InvLinkedGoodsLocation' }}
        />
        <DataTable.Column
          colKey="AltGoodsLocation"
          header="ALT Goods Location"
          searchOpts={{ type: 'text', sendAs: 'filter_AltGoodsLocation' }}
        />
        <DataTable.Column
          colKey="actions"
          header=""
          hideFilter
          children={(item) => (
            <Dropdown className="w-100 h-100 d-grid center">
              <Dropdown.Toggle as="button" className="dot-menu-button">
                <EllipsisV width={'0.5rem'} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as="button" onClick={() => setSelectedPort(item)}>
                  View/Edit
                </Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => setShowConfirmDelete(item.id)}>
                  Remove
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        />
      </DataTable>
      <ConfirmationModal
        header="Confirm Deletion"
        show={!!showConfirmDelete}
        onHide={() => setShowConfirmDelete(null)}
        onConfirm={() => handleDelete(showConfirmDelete)}
      >
        Are you sure you want to delete this record?
      </ConfirmationModal>
    </>
  );
};

export default Port;
